import dayjs from 'dayjs';
import { useQuery, useQueryClient } from 'react-query';

import {
    type ILead,
    apiClient,
    Auth,
    queryKeys,
    useLoggedInUser,
} from 'src/services/api';

export const usePollLeads = () => {
    const { data: loggedInUser } = useLoggedInUser();

    const queryClient = useQueryClient();

    const jwtPayload = Auth.jwtPayload;
    const isBypassPaywall = jwtPayload?.ibp;
    const isPaywallEnabled = !isBypassPaywall && !loggedInUser?.team?.is_active;

    return useQuery(
        [
            'poll-leads',
            loggedInUser?.uuid,
            isPaywallEnabled ? 'paywall' : 'no-paywall',
        ],
        async () => {
            const res = await apiClient('api/landlord/leads', {
                searchParams: {
                    // This sends the correct datetime that is the user's local time converted to UTC
                    // Removing the timezone part, because backend cannot handle dates with timezones
                    since: dayjs().subtract(30, 'seconds').toISOString().split('Z')[0],
                },
            }).json<{ leads: ILead[] }>();

            const updatedLeads = res.leads;
            if (updatedLeads.length === 0) {
                return;
            }

            queryClient.setQueryData<ILead[]>([queryKeys.leads], (_oldLeads) => {
                const oldLeads = _oldLeads || [];

                return Array.from(new Map([...oldLeads, ...updatedLeads].map((o) => [o.uuid, o])).values());
            });

            for (const lead of updatedLeads) {
                queryClient.setQueryData([queryKeys.leads, lead.uuid], lead);
            }
        },
        {
            enabled: !!loggedInUser?.is_onboarding_complete && !isPaywallEnabled,
            refetchInterval: 15000,
        }
    );
};
